import * as Yup from 'yup';
import * as R from 'ramda';
import { withFormik } from 'formik';
import React, { Fragment } from 'react';
import { pure, compose, lifecycle, withState, withProps, withHandlers } from 'react-recompose';
// components
import { InfoPair } from '../../../components/info-pair';
import { ChargeFormFooter } from '../../../components/form-footer';
import PriceSheetItems from '../../../components/price-sheet-items';
import { ChargesTotal } from '../../../components/charge/components';
import { withRecalculateAllChargesId } from '../../../components/charge/hocs';
import { DMChargeComponent } from '../../../components/charge/formik/charges-array';
import {
  withAsyncChargeAccessorials,
  getTelSharedAccessorialsApplyTo,
} from '../../../components/charge/hocs/with-async-charge-accessorials';
// forms
import { Fieldset2 } from '../../../forms';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hocs
import {
  withAsyncConfigs,
  withAsyncMarginViolationRules,
  withConnectModalAndLoaderActions,
} from '../../../hocs';
// ui
import {
  Box,
  Flex,
  Span,
  StickedBox,
  ModalActionButton,
  ModalCancelButton,
  MainDarkActionButton,
} from '../../../ui';
// schemes
import * as VS from '../../../schemes';
// feature rate
import ShowScorecards from './scorecards';
import ShowLaneHistory from './lane-history';
import CarrierSection from './carrier-section';
import AvailableRates from './available-rates';
import DispatcherSection from './dispatcher-section';
import { withAsyncContacts, withAsyncCarrierRates } from '../hocs';
import TrackingReferenceSection from './trucking-references-section';
import { mainCarrierRateFields, totalCarrierRateFields } from './settings';
import MarginViolationSection from '../components/margin-violation-section';
import { withPreselectedTrailers } from '../hooks/with-preselected-trailers';
import { NotAllowedCustomerRate } from '../components/not-allowed-customer-rate';
import {
  getChargesFromValues,
  getMarginViolationData,
  setDefaultCarrierRateValues,
  getTotalQuantitiesFromValues,
} from '../helpers';
//////////////////////////////////////////////////

const blackColor = G.getTheme('colors.black');

const renderIntegrationCarriers = ({ originCarrierName, destinationCarrierName, transportingCarrierName }) => {
  if (G.isOneNotNilOrNotEmpty([originCarrierName, destinationCarrierName])) {
    return (
      <Fragment>
        {G.getWindowLocale(
          'message:rate-has-origin-destination-carriers',
          'Warning, this rate has the following carriers',
        )}:
        <Flex mt='4px' maxWidth='90vw' flexWrap='wrap'>
          <InfoPair
            width='unset'
            fontSize={16}
            marginRight={10}
            lineHeight='initial'
            textColor={blackColor}
            text={originCarrierName}
            textMaxWidth='calc(90vw - 200px)'
            label={G.getWindowLocale('titles:origin-carrier-name', 'Origin Carrier Name')}
          />
          <InfoPair
            pt='8px'
            width='unset'
            fontSize={16}
            lineHeight='initial'
            textColor={blackColor}
            text={destinationCarrierName}
            textMaxWidth='calc(90vw - 200px)'
            label={G.getWindowLocale('titles:destination-carrier-name', 'Destination Carrier Name')}
          />
        </Flex>
        {G.getWindowLocale('messages:do-you-want-to-continue', 'Do you want to continue')}?
      </Fragment>
    );
  }

  return (
    <Fragment>
      {G.getWindowLocale(
        'message:rate-has-transporting-carrier',
        'Warning, this rate has the following Transporting Carrier',
      )}:
      <Span ml='5px' fontWeight='bold'>{transportingCarrierName}</Span>
      <br />{G.getWindowLocale('messages:do-you-want-to-continue', 'Do you want to continue')}?
    </Fragment>
  );
};

const SelectRateFromIntegration = ({
  closeModal,
  handleSelect,
  originCarrierName,
  destinationCarrierName,
  transportingCarrierName,
}) => (
  <Box fontSize={16} lineHeight={1.8} textAlign='center'>
    {renderIntegrationCarriers({
      originCarrierName,
      destinationCarrierName,
      transportingCarrierName,
    })}
    <Flex mt={15} justifyContent='space-around'>
      <ModalCancelButton onClick={closeModal}>
        {G.getWindowLocale('titles:no', 'No')}
      </ModalCancelButton>
      <ModalActionButton onClick={handleSelect}>
        {G.getWindowLocale('titles:yes', 'Yes')}
      </ModalActionButton>
    </Flex>
  </Box>
);

const validationSchemaObject = Yup.object()
  .shape(VS.validationSchemaCarrierObject)
  .shape(VS.validationSchemaTripTotalObject)
  .shape(VS.validationSchemaTripServiceObject)
  .shape(VS.validationSchemaDispatcherInfoObject)
  .shape(VS.validationSchemaMarginViolationObject);

const validationUpdateSchemaObject = Yup.object()
  .shape(VS.validationSchemaCarrierObject)
  .shape(VS.validationSchemaTripTotalObject)
  .shape(VS.validationSchemaTripServiceObject)
  .shape(VS.validationSchemaDispatcherInfoObject)
  .shape(VS.validationSchemaMarginViolationObject);

const enhanceCommon = compose(
  withState('notAllowedCustomerRate', 'setNotAllowedCustomerRate', ({ notAllowCustomerRateInit }) => (
    R.or(notAllowCustomerRateInit, false)
  )),
  lifecycle({
    componentDidMount() {
      const { getTelRateCarrierRatingInfoRequest } = this.props;

      getTelRateCarrierRatingInfoRequest(this.props);
    },
  }),
  withAsyncMarginViolationRules,
);

const mapCharges = (charges) => R.map(
  (item) => R.assoc(GC.FIELD_ID, R.or(G.getGuidFromObject(item), G.genShortId()), item),
  G.addOneDiscountChargeToCharges(charges),
);

const getCarrierRateFields = (data) => {
  const {
    quoteNumber,
    carrierSnapshot,
    originCarrierName,
    configAccountNumber,
    transportingCarrier,
    destinationCarrierName,
  } = data;

  const transportingCarrierForCarrierAssignment = R.compose(
    R.pick(['quoteId', 'quoteShipmentId', 'transportingCarrierScac', GC.FIELD_TRANSPORTING_CARRIER_NAME]),
    G.renameKeys({
      [GC.FIELD_SCAC]: 'transportingCarrierScac',
      [GC.FIELD_NAME]: GC.FIELD_TRANSPORTING_CARRIER_NAME,
    }),
  )(transportingCarrier);

  return {
    ...carrierSnapshot,
    ...transportingCarrierForCarrierAssignment,
    quoteNumber,
    originCarrierName,
    destinationCarrierName,
    [GC.FIELD_INTEGRATION_ACCOUNT_NUMBER]: configAccountNumber,
    [GC.FIELD_CARRIER_GUID]: G.getGuidFromObject(carrierSnapshot),
  };
};

const handleCustomChange = (props) => (e) => {
  const { values, setValues, isEditMode, sharedAccessorialsApplyTo } = props;

  const { carrierRateCharges } = values;

  const value = G.getEventTargetValue(e);

  const sharedApplyToFiltered = R.or(isEditMode, G.isNilOrEmpty(R.prop(GC.FIELD_MODE, values))) ?
    [] : G.filterChargesByMode(sharedAccessorialsApplyTo, value);

  setValues({
    ...values,
    [GC.FIELD_MODE]: value,
    [GC.FIELD_CARRIER_RATE_CHARGES]: G.isNilOrEmpty(carrierRateCharges) ?
      sharedApplyToFiltered : [...G.filterChargesByModeOrNoMode(carrierRateCharges, value), ...sharedApplyToFiltered],
  });
};

const enhanceCreate = compose(
  withRecalculateAllChargesId,
  withAsyncCarrierRates,
  withAsyncContacts,
  withPreselectedTrailers,
  withState('isValidEmails', 'setIsValidEmails', true),
  withState('isOpenedDispatcherInfoSection', 'setIsOpenedDispatcherInfoSection', false),
  enhanceCommon,
  withFormik({
    enableReinitialize: true,
    validationSchema: validationSchemaObject,
    mapPropsToValues: (props) => setDefaultCarrierRateValues(props),
    handleSubmit: (values, { props }) => {
      const { shouldSelect, handleSendTelRate } = props;

      let data = G.ifElse(
        G.isBoolean(shouldSelect),
        R.assoc('selected', shouldSelect, values),
        values,
      );

      const { marginViolated, maxPayViolated } = getMarginViolationData({ ...props, values, isCarrier: true });

      if (R.and(R.not(marginViolated), R.not(maxPayViolated))) {
        data = R.mergeRight(data, {
          [GC.FIELD_MARGIN_VIOLATION_NOTE]: null,
          [GC.FIELD_MARGIN_VIOLATION_REASON]: null,
        });
      }

      handleSendTelRate(data);
    },
    displayName: 'ADD_CARRIER_RATE_FORM',
  }),
  withState(
    'selectedCarrier',
    'setSelectedCarrier',
    ({ values }) => R.prop(GC.SYSTEM_OBJECT_CARRIER_ASSIGNMENT, values),
  ),
  withState('selectedFleetTrailer', 'setSelectedFleetTrailer', null),
  withHandlers({
    // NOTE: remember common logic with withHandleCloRateAvailableRates -> handleSelectRate -> setCarrierRateToStore
    handleSelectAutoRate: (props) => (data) => {
      const {
        values,
        setValues,
        openModal,
        closeModal,
        setSelectedCarrier,
        handleGetContractAccessorials,
        handleSetRecalculateAllChargesId,
      } = props;

      const {
        charges,
        originCarrierName,
        destinationCarrierName,
      } = data;

      const carrierRateFields = getCarrierRateFields(data);

      const carrierAssignment = {
        ...R.prop(GC.SYSTEM_OBJECT_CARRIER_ASSIGNMENT, props),
        ...carrierRateFields,
        [GC.FIELD_FLEET_TRAILER_GUID]:
          R.pathOr(null, [GC.SYSTEM_OBJECT_CARRIER_ASSIGNMENT, GC.FIELD_FLEET_TRAILER_GUID], values),
      };

      const mode = R.or(
        R.path(['price', GC.FIELD_MODE, GC.FIELD_DROPDOWN_OPTION_GUID], data),
        R.path([GC.FIELD_MODE], values),
      );

      const serviceType = R.pathOr(
        '',
        [GC.FIELD_DROPDOWN_OPTION_GUID],
        R.head(R.pathOr([], [GC.FIELD_PRICE, GC.FIELD_CARRIER_SERVICE_TYPES], data)),
      );

      const carrierContractGuid = R.path(['price', 'contractGuid'], data);

      const cloRateAdjustments = R.path(['rateInfo', GC.FIELD_CLO_RATE_ADJUSTMENTS], props);

      const staticFields = R.pick(
        [
          GC.FIELD_CURRENCY,
          GC.FIELD_SERVICE_DAYS,
          GC.FIELD_REQUESTED_PICKUP_EARLY_DATE,
          GC.FIELD_ESTIMATED_DELIVERY_LATE_DATE,
          GC.FIELD_ESTIMATED_DELIVERY_EARLY_DATE,
        ],
        data,
      );

      const newValues = {
        ...values,
        ...staticFields,
        carrierContractGuid,
        [GC.FIELD_MODE]: mode,
        [GC.FIELD_SERVICE_TYPE]: serviceType,
        [GC.FIELD_CLO_RATE_ADJUSTMENTS]: cloRateAdjustments,
        [GC.FIELD_CARRIER_RATE_CHARGES]: mapCharges(charges),
        [GC.SYSTEM_OBJECT_CARRIER_ASSIGNMENT]: R.omit([GC.FIELD_GUID], carrierAssignment),
      };

      const transportingCarrierName = R.path([GC.FIELD_TRANSPORTING_CARRIER, GC.FIELD_NAME], data);

      if (G.isOneNotNilOrNotEmpty([originCarrierName, destinationCarrierName, transportingCarrierName])) {
        const handleSelect = () => {
          setValues(newValues);
          setSelectedCarrier(carrierAssignment);
          G.callFunctionWith2Args(handleGetContractAccessorials, data.contractGuid || carrierContractGuid, charges);

          G.callFunction(handleSetRecalculateAllChargesId);
          G.callFunctionTwice(closeModal);
        };

        const component = (
          <SelectRateFromIntegration
            closeModal={closeModal}
            handleSelect={handleSelect}
            originCarrierName={originCarrierName}
            destinationCarrierName={destinationCarrierName}
            transportingCarrierName={transportingCarrierName}
          />
        );

        const modal = {
          p: 15,
          component,
          options: {},
        };

        return openModal(modal);
      }

      setValues(newValues);
      setSelectedCarrier(carrierAssignment);
      G.callFunctionWith2Args(handleGetContractAccessorials, data.contractGuid || carrierContractGuid, charges);

      G.callFunction(handleSetRecalculateAllChargesId);

      closeModal();
    },
  }),
  withHandlers({
    handleOpenAutoRateModal: (props) => () => {
      const {
        tel,
        values,
        telGuid,
        loadData,
        openModal,
        branchGuid,
        closeModal,
        handleSelectAutoRate,
        customerEnterpriseGuid,
      } = props;

      const transportationMode = R.path([GC.FIELD_MODE], values);
      const serviceType = R.path([GC.FIELD_SERVICE_TYPE], values);

      const equipments = R.map(
        R.prop(GC.FIELD_DROPDOWN_OPTION_GUID),
        R.pathOr([], [GC.FIELD_TEL, GC.FIELD_CARRIER_EQUIPMENTS], props),
      );

      const modalContent = (
        <AvailableRates
          tel={tel}
          telGuid={telGuid}
          loadData={loadData}
          openModal={openModal}
          branchGuid={branchGuid}
          closeModal={closeModal}
          equipments={equipments}
          serviceType={serviceType}
          onSelectRate={handleSelectAutoRate}
          transportationMode={transportationMode}
          customerEnterpriseGuid={customerEnterpriseGuid}
        />
      );

      const modal = {
        p: '0',
        component: modalContent,
        options: {
          width: 'auto',
          minWidth: 500,
          height: 'auto',
          overflow: 'auto',
          maxWidth: '90vw',
          maxHeight: '80vh',
          minHeight: '50vh',
        },
      };

      openModal(modal);
    },
    handleCustomChange,
  }),
  withProps({
    isCarrier: true,
    isEditMode: false,
    isCreateMode: true,
    title: G.getWindowLocale('titles:add-carrier-rate', 'Add Carrier Rate'),
  }),
  pure,
);

const handleUpdateCarrierRate = (values, props) => {
  const { handleSendTelRate } = props;

  const { marginViolated, maxPayViolated } = getMarginViolationData({ ...props, values, isCarrier: true });

  const fieldsToOmit = [
    GC.FIELD_MAX_PAY_VIOLATED,
    GC.FIELD_MIN_MARGIN_VIOLATED,
    GC.FIELD_CRITICAL_MARGIN_VIOLATED,
  ];

  const valuesToUse = R.omit(fieldsToOmit, values);

  const data = G.ifElse(
    R.or(marginViolated, maxPayViolated),
    valuesToUse,
    R.mergeRight(valuesToUse, {
      [GC.FIELD_MARGIN_VIOLATION_NOTE]: null,
      [GC.FIELD_MARGIN_VIOLATION_REASON]: null,
    }),
  );

  const violationFields = [GC.FIELD_MIN_MARGIN_VIOLATED, GC.FIELD_CRITICAL_MARGIN_VIOLATED];

  handleSendTelRate(R.omit(violationFields, data));
};

const enhanceUpdate = compose(
  withRecalculateAllChargesId,
  withAsyncCarrierRates,
  withAsyncContacts,
  withState('isValidEmails', 'setIsValidEmails', true),
  withState('selectedFleetTrailer', 'setSelectedFleetTrailer', null),
  enhanceCommon,
  withFormik({
    enableReinitialize: true,
    displayName: 'EDIT_CARRIER_RATE_FORM',
    validationSchema: validationUpdateSchemaObject,
    mapPropsToValues: (props) => setDefaultCarrierRateValues(props),
    handleSubmit: (values, { props }) => handleUpdateCarrierRate(values, props),
  }),
  withHandlers({
    handleCustomChange,
    handleGetSharedAccessorialsApplyTo: (props) => async () => {
      const {
        items,
        values,
        telGuid,
        loadData,
        setValues,
        branchGuid,
        accessorials,
        matrixAccessorials,
        handleSetRecalculateAllChargesId,
      } = props;

      const sharedApplyTo = await getTelSharedAccessorialsApplyTo({
        items,
        telGuid,
        loadData,
        branchGuid,
        matrixAccessorials,
        accessorials: R.values(accessorials),
        stopCount: R.pathOr(0, ['rateInfo', 'stopCount'], props),
        applyToType: GC.SHARED_ACCESSORIAL_AUTO_APPLY_TO_TYPE_CARRIER,
      });

      const sharedApplyToFiltered = G.filterChargesByModeOrNoMode(sharedApplyTo, R.prop(GC.FIELD_MODE, values));

      const carrierRateCharges = R.pathOr([], [GC.FIELD_CARRIER_RATE_CHARGES], values);
      const charges = G.replaceAutoChargesWithNewSharedAssessorialsApplyTo(carrierRateCharges, sharedApplyToFiltered);
      const newValues = R.assoc(GC.FIELD_CARRIER_RATE_CHARGES, charges, values);

      setValues(newValues);

      G.callFunction(handleSetRecalculateAllChargesId);
    },
  }),
  withState('carrierRate', 'setCarrierRate', 0),
  withProps({
    isCarrier: true,
    isEditMode: true,
    isCreateMode: false,
    title: G.getWindowLocale('titles:edit-carrier-rate', 'Edit Carrier Rate'),
  }),
  pure,
);

const isShownDispatcherSection = R.compose(
  R.lt(0),
  R.length,
  R.values,
  R.filter(G.isNotNilAndNotEmpty),
  R.pick([GC.FIELD_DISPATCH_NAME, GC.FIELD_DISPATCH_PHONE, GC.FIELD_DISPATCH_EMAILS]),
);

const getCommonChargeProps = (props) => {
  const currency = R.pathOr(
    R.pathOr(0, ['orderRateInfo', 'currency'], props),
    ['values', GC.FIELD_CURRENCY],
    props,
  );

  const customerLineHaul = R.pathOr(
    R.pathOr(0, ['orderRateInfo', 'customerLineHaul'], props),
    ['rateInfo', 'customerLineHaul'],
    props,
  );

  const totalCustomersRate = R.pathOr(
    R.pathOr(0, ['orderRateInfo', GC.FIELD_TOTAL_CUSTOMER_RATE], props),
    ['rateInfo', GC.FIELD_TOTAL_CUSTOMER_RATE],
    props,
  );

  const stopsQuantity = R.pathOr(0, ['rateInfo', 'stopCount'], props);
  const cloRateAdjustments = R.pathOr([], ['rateInfo', 'cloRateAdjustments'], props);
  const normalizedTotal = R.pathOr({}, ['values', GC.SYSTEM_OBJECT_NORMALIZED_TOTAL], props);

  const totalQuantities = getTotalQuantitiesFromValues(
    R.prop('values', props),
    R.path(['rateInfo', GC.FIELD_TOTAL_QUANTITIES], props),
  );

  return {
    currency,
    stopsQuantity,
    normalizedTotal,
    totalQuantities,
    customerLineHaul,
    totalCustomersRate,
    cloRateAdjustments,
  };
};

const CarrierCharges = (props) => {
  const { branchGuid, eventDates, isEditMode, isCreateMode, recalculateAllChargesId } = props;

  const commonTotalProps = G.getTotalsFromValues(props);
  const commonChargeProps = getCommonChargeProps(props);

  return (
    <div>
      <DMChargeComponent
        {...commonTotalProps}
        {...commonChargeProps}
        rateProps={props}
        isCarrier={true}
        isCarrierRate={true}
        eventDates={eventDates}
        branchGuid={branchGuid}
        loadType={GC.FIELD_TEL}
        isEditMode={isEditMode}
        isCreateMode={isCreateMode}
        chargesArrayName={GC.FIELD_CARRIER_RATE_CHARGES}
        recalculateAllChargesId={recalculateAllChargesId}
      />
    </div>
  );
};

const CarrierRateForm = (props) => {
  const {
    tel,
    title,
    stops,
    values,
    telGuid,
    loadData,
    setValues,
    openModal,
    closeModal,
    branchGuid,
    openLoader,
    isEditMode,
    closeLoader,
    contactList,
    isCreateMode,
    handleSubmit,
    equipmentGuid,
    initialValues,
    isValidEmails,
    submitBtnStyles,
    cancelBtnStyles,
    setIsValidEmails,
    setSelectedCarrier,
    handleCustomChange,
    preselectedTrailers,
    selectedFleetTrailer,
    notAllowedCustomerRate,
    setSelectedFleetTrailer,
    handleOpenAutoRateModal,
    handleSetRecalculateAllChargesId,
    handleGetSharedAccessorialsApplyTo,
  } = props;

  const { marginViolated, maxPayViolated, criticalMarginViolated } = getMarginViolationData(props);

  const configCarrierUseFleetTrailer = G.getConfigCarrierUseFleetTrailerFromWindow();
  const carrierName = R.path([GC.SYSTEM_OBJECT_CARRIER_ASSIGNMENT, GC.FIELD_NAME], values);
  const carrierGuid = R.path([GC.SYSTEM_OBJECT_CARRIER_ASSIGNMENT, GC.FIELD_CARRIER_GUID], values);

  const eventDates = G.getFirstLastEventDateFromLoad(tel, GC.LOAD_TYPE_TEL, props.asyncConfigs);

  return (
    <Box width={835} bg='white'>
      <StickedBox top='0px' zIndex={15}>
        <Flex
          p='7px 10px'
          bg='offWhite'
          color='dark.mainDark'
          justifyContent='space-between'
        >
          <Box fontWeight={700}>
            {title}
          </Box>
          <Flex gap='5px'>
            {
              G.isNotNilAndNotEmpty(carrierGuid) &&
              <ShowScorecards
                openModal={openModal}
                branchGuid={branchGuid}
                carrierGuid={carrierGuid}
                carrierName={carrierName}
              />
            }
            <MainDarkActionButton onClick={handleSetRecalculateAllChargesId}>
              {G.getWindowLocale('titles:recalc-all-charges', 'Recalc All Charges')}
            </MainDarkActionButton>
            {
              isEditMode &&
              <MainDarkActionButton onClick={handleGetSharedAccessorialsApplyTo}>
                {G.getWindowLocale('titles:update-auto-acc', 'Update Auto Acc.')}
              </MainDarkActionButton>
            }
            {
              isCreateMode &&
              <MainDarkActionButton onClick={handleOpenAutoRateModal}>
                {G.getWindowLocale('titles:show-rates', 'Show Rates')}
              </MainDarkActionButton>
            }
            {
              R.and(isCreateMode, G.getAmousConfigByNameFromWindow(GC.UI_TEL_CARRIER_RATE_SHOW_LANE_HISTORY)) &&
              <ShowLaneHistory
                {...G.getModeServiceFromAsyncConfigsForRateSelect(props)}
                stops={stops}
                openModal={openModal}
                setValues={setValues}
                openLoader={openLoader}
                branchGuid={branchGuid}
                closeModal={closeModal}
                closeLoader={closeLoader}
                equipmentGuid={equipmentGuid}
                setSelectedCarrier={setSelectedCarrier}
                transportationModeGuid={R.prop(GC.FIELD_MODE, values)}
                serviceTypeGuid={R.prop(GC.FIELD_SERVICE_TYPE, values)}
              />
            }
          </Flex>
        </Flex>
      </StickedBox>
      <form onSubmit={handleSubmit}>
        <MarginViolationSection
          {...G.getFormikProps(props)}
          marginViolated={marginViolated}
          maxPayViolated={maxPayViolated}
          criticalMarginViolated={criticalMarginViolated}
          optionsForSelect={{
            marginViolationReasonOptions: G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
              props.asyncConfigs,
              GC.RATE_ENGINE_MARGIN_VIOLATION_REASON,
              true,
            ),
          }}
        />
        <CarrierSection {...props} initialCarrier={R.path(['selectedCarrier'], props)} />
        <DispatcherSection
          {...G.getFormikProps(props)}
          telGuid={telGuid}
          loadData={loadData}
          openModal={openModal}
          branchGuid={branchGuid}
          closeModal={closeModal}
          contactList={contactList}
          setIsValidEmails={setIsValidEmails}
          preselectedTrailers={preselectedTrailers}
          selectedFleetTrailer={selectedFleetTrailer}
          isEditMode={isShownDispatcherSection(values)}
          useFleetTrailer={configCarrierUseFleetTrailer}
          setSelectedFleetTrailer={setSelectedFleetTrailer}
          fieldError={R.path(['errors', GC.FIELD_DISPATCH_EMAILS], props)}
          emails={R.pathOr([], ['values', GC.FIELD_DISPATCH_EMAILS], props)}
        />
        <TrackingReferenceSection {...G.getFormikProps(props)} initialValues={initialValues} />
        <Fieldset2
          {...G.getFormikProps(props)}
          fields={mainCarrierRateFields}
          handleCustomChange={handleCustomChange}
          fieldsWrapperStyles={{ pt: 20, px: 15 }}
          {...G.getModeServiceFromAsyncConfigsForRateSelect(props)}
        />
        <Fieldset2
          {...G.getFormikProps(props)}
          fields={totalCarrierRateFields}
          fieldsWrapperStyles={{ px: 15, mb: 20 }}
        />
        {
          isEditMode && <PriceSheetItems values={values} />
        }
        <CarrierCharges {...props} eventDates={eventDates} />
        <StickedBox bottom='0px' zIndex={13}>
          <ChargesTotal {...props} charges={getChargesFromValues(props)} />
          <ChargeFormFooter
            submitBtnStyles={submitBtnStyles}
            cancelBtnStyles={cancelBtnStyles}
            submitDisabled={R.or(R.not(isValidEmails), notAllowedCustomerRate)}
          />
          <NotAllowedCustomerRate notAllowedCustomerRate={notAllowedCustomerRate} />
        </StickedBox>
      </form>
    </Box>
  );
};

const enhanceCreateWithConfigs = compose(
  withConnectModalAndLoaderActions,
  withAsyncConfigs,
  withAsyncChargeAccessorials({ isCarrier: true, isCreateMode: true }),
  enhanceCreate,
);

const enhanceUpdateWithConfigs = compose(
  withConnectModalAndLoaderActions,
  withAsyncConfigs,
  withAsyncChargeAccessorials({ isCarrier: true, isCreateMode: false }),
  enhanceUpdate,
);

export default enhanceCreateWithConfigs(CarrierRateForm);

const EditCarrierRateForm = enhanceUpdateWithConfigs(CarrierRateForm);

export {
  CarrierRateForm,
  EditCarrierRateForm,
  getCarrierRateFields,
  handleUpdateCarrierRate,
  enhanceUpdateWithConfigs,
  SelectRateFromIntegration,
};
